import { useEffect, type FormEvent, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Search, MessageSquare, Clock, X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { tr, enUS, it } from "date-fns/locale";
import { cn } from "@/lib/utils";
import {
  PaletteFooter,
  PaletteKbd,
  PaletteShell,
} from "@/components/overlays/palette-shell";
import { useLanguage } from "@/store/use-ui-preferences-store";
import type { Conversation } from "@/api/models/api-types";
import { useConversationModule } from "@/features/conversations/hooks/use-conversation-module";
import { useGlobalSearch } from "../hooks/use-global-search";

const highlightText = (text: string, query: string) => {
  if (!query) return text;

  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark
        key={i}
        className="rounded-sm bg-module-accent-soft text-module-accent"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

const SearchConversationRow = ({
  conversation,
  query,
  dateLabel,
  onSelect,
}: {
  conversation: Conversation;
  query: string;
  dateLabel: string;
  onSelect: (e: MouseEvent<HTMLAnchorElement>) => void;
}) => {
  const { t } = useTranslation();
  const { module, scopeStyle } = useConversationModule(conversation);
  const ModuleIcon = module?.icon;

  return (
    <Link
      to={`/chat/${conversation._id}`}
      onClick={onSelect}
      style={scopeStyle}
      className={cn(
        "module-scope group flex w-full items-center gap-3 rounded-2xl border border-transparent px-3 py-2.5",
        "transition-[background-color,border-color,box-shadow] duration-200",
        "hover:border-module-accent/30 hover:bg-module-accent-soft hover:shadow-[0_14px_32px_-22px_var(--module-accent)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-module-accent/50",
      )}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-module-accent-soft text-module-accent transition-transform duration-300 group-hover:scale-105">
        {ModuleIcon ? <ModuleIcon size={18} /> : <MessageSquare size={18} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-text-primary dark:text-gray-100">
          {highlightText(conversation.name || t("chat.untitled"), query)}
        </span>
        <span className="mt-0.5 flex items-center gap-2 text-xs text-text-tertiary dark:text-gray-400">
          <span>{dateLabel}</span>
          {module && (
            <>
              <span aria-hidden>•</span>
              <span className="truncate">{t(module.nameKey)}</span>
            </>
          )}
        </span>
      </span>
    </Link>
  );
};

export function GlobalSearchDialog() {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const {
    searchQuery,
    searchHistory,
    isSearchOpen,
    conversations,
    isLoading,
    onSearchQueryChange,
    onSearchSubmit,
    onConversationSelect,
    onSelectFromHistory,
    onClearQuery,
    onCloseSearch,
    onClearHistory,
  } = useGlobalSearch();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
      }

      if (e.key === "Escape" && isSearchOpen) {
        onCloseSearch();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchOpen, onCloseSearch]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSearchSubmit(searchQuery);
  };

  const formatConversationDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return format(date, "HH:mm", {
        locale: language === "Turkish" ? tr : language === "Italian" ? it : enUS,
      });
    } else if (diffDays === 1) {
      return t("chat.yesterday");
    } else if (diffDays < 7) {
      return format(date, "EEEE", {
        locale: language === "Turkish" ? tr : language === "Italian" ? it : enUS,
      });
    } else {
      return format(date, "dd/MM/yyyy", {
        locale: language === "Turkish" ? tr : language === "Italian" ? it : enUS,
      });
    }
  };

  const hasQuery = searchQuery !== "";
  const showHistory = !hasQuery && searchHistory.length > 0;
  const showEmptyHint = !hasQuery && searchHistory.length === 0;

  return (
    <Dialog open={isSearchOpen} onOpenChange={onCloseSearch}>
      <DialogContent
        className="palette-dialog overflow-visible border-none bg-transparent p-0 shadow-none after:hidden"
        overlayClassName="bg-black/45 backdrop-blur-md dark:bg-black/60"
        open={isSearchOpen}
      >
        <PaletteShell
          footer={
            <PaletteFooter>
              <span className="inline-flex items-center gap-1.5">
                <PaletteKbd>↵</PaletteKbd>
                {t("common.select")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <PaletteKbd>esc</PaletteKbd>
                {t("common.close")}
              </span>
            </PaletteFooter>
          }
        >
          <DialogTitle className="sr-only">{t("chat.search")}</DialogTitle>

          <motion.form
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-3 border-b border-module-accent/10 px-4 py-3.5 dark:border-white/[0.06]"
            onSubmit={handleSubmit}
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-module-accent-soft text-module-accent">
              <Search className="size-4" />
            </span>
            <input
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              placeholder={t("chat.searchPlaceholder")}
              className="h-9 flex-1 bg-transparent text-base text-text-primary placeholder:text-text-tertiary focus:outline-none dark:text-gray-100 dark:placeholder:text-gray-500"
              autoFocus
            />
            {isLoading && (
              <Loader2 className="size-4 shrink-0 animate-spin text-module-accent" />
            )}
            {hasQuery && (
              <button
                type="button"
                onClick={onClearQuery}
                className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-module-accent-soft hover:text-module-accent dark:text-gray-400"
              >
                <X className="size-4" />
                <span className="sr-only">{t("chat.clearSearch")}</span>
              </button>
            )}
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.06 }}
            className="max-h-[50vh] overflow-y-auto sm:max-h-[60vh]"
            style={{ scrollbarWidth: "thin" }}
          >
            <AnimatePresence mode="wait">
              {showHistory ? (
                <motion.div
                  key="recent"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="p-2"
                >
                  <div className="mb-1 flex items-center justify-between px-2 py-1.5">
                    <div className="flex items-center gap-2 text-xs font-medium text-text-tertiary dark:text-gray-400">
                      <Clock className="size-3.5" />
                      {t("chat.recentSearches")}
                    </div>
                    <button
                      type="button"
                      onClick={onClearHistory}
                      className="text-xs text-text-tertiary transition-colors hover:text-module-accent dark:text-gray-500"
                    >
                      {t("chat.deleteSearchHistory")}
                    </button>
                  </div>
                  <div className="flex flex-col gap-1">
                    {searchHistory.map((search, index) => (
                      <motion.button
                        key={search}
                        type="button"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.18, delay: index * 0.03 }}
                        className="group flex w-full items-center gap-3 rounded-2xl border border-transparent px-3 py-2.5 text-left transition-[background-color,border-color] duration-200 hover:border-module-accent/20 hover:bg-module-accent-soft"
                        onClick={() => onSelectFromHistory(search)}
                      >
                        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-module-accent-soft text-module-accent">
                          <Search className="size-4" />
                        </span>
                        <span className="truncate text-sm font-medium text-text-primary dark:text-gray-100">
                          {search}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              ) : hasQuery ? (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="p-2"
                >
                  <div className="mb-1 flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-text-tertiary dark:text-gray-400">
                    <MessageSquare className="size-3.5" />
                    {t("chat.searchResults")} ({conversations.length})
                  </div>
                  {conversations.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
                      <span className="flex size-11 items-center justify-center rounded-2xl bg-module-accent-soft text-module-accent">
                        <Search className="size-5" />
                      </span>
                      <p className="text-sm text-text-tertiary dark:text-gray-400">
                        {isLoading
                          ? t("common.loading")
                          : t("chat.noResults")}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {conversations.map((conversation, index) => (
                        <motion.div
                          key={conversation._id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.18, delay: index * 0.03 }}
                        >
                          <SearchConversationRow
                            conversation={conversation}
                            query={searchQuery}
                            dateLabel={formatConversationDate(
                              conversation.updatedAt,
                            )}
                            onSelect={onConversationSelect}
                          />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ) : showEmptyHint ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center gap-3 px-4 py-12 text-center"
                >
                  <span className="flex size-12 items-center justify-center rounded-2xl bg-module-accent-soft text-module-accent">
                    <Search className="size-5" />
                  </span>
                  <p className="max-w-xs text-sm text-text-tertiary dark:text-gray-400">
                    {t("chat.searchEmptyHint")}
                  </p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        </PaletteShell>
      </DialogContent>
    </Dialog>
  );
}
